import { Fragment, useCallback, useMemo } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { ChevronDown, ChevronRight } from "lucide-react";

import { WithheldCount } from "@app/components/WithheldCount";
import { groupColor } from "@app/lib/palette";
import { cn } from "@app/lib/utils";
import type {
  CompositionNode,
  CompositionResponse,
  PathwayDimension,
} from "@app/types/dashboard-pathway";
import { valueLabeller } from "@app/utils/value-labels";

/**
 * The eight forms a composition card can take.
 *
 * They share one discipline, and it is the discipline rather than the shapes that makes them
 * readable: ONE scale per card from top to bottom, the number WRITTEN at the end of the bar instead
 * of read off an axis, whatever is cut always announced with its size, and the accent spent in a
 * single place. A sub-group drawn on its own scale looks as big as its parent; a bar with no number
 * makes the reader estimate; a silent truncation reads as "that was all".
 *
 * Nothing here is a charting library: percentage-width divs and a few polygons, which is what the
 * rest of the app already does.
 */

export interface RepresentationProps {
  data: CompositionResponse;
  mode: "n" | "pct";
  /** A click on a label filters the whole page — never just this card. */
  onPick: (dimension: string, value: string) => void;
  /** Paths currently open, for the nested form. */
  expanded: Set<string>;
  onToggleExpand: (path: string) => void;
}

/**
 * Every value a card draws is a TERM: a click filters on the term, and the reader sees it in their
 * own language. Read off the card's own dimensions, which carry their terms' labels.
 */
function useLabelOf(data: CompositionResponse) {
  return useMemo(() => valueLabeller(data.dimensions), [data.dimensions]);
}

const isTruncated = (node: CompositionNode) => node.truncated === true;
const realNodes = (nodes: CompositionNode[]) => nodes.filter((n) => !isTruncated(n));
const truncationOf = (nodes: CompositionNode[]) => nodes.find(isTruncated) ?? null;
/** The dimension a level's values belong to, read off the first of them. */
const dimensionOf = (nodes: CompositionNode[]) =>
  realNodes(nodes)[0]?.dimension ?? "";

/** The count that sets the scale: the largest first-level branch, so every bar shares one ruler. */
function topScale(nodes: CompositionNode[]): number {
  return Math.max(1, ...realNodes(nodes).map((n) => n.episodes ?? 0));
}

/**
 * How a count is written under each mode.
 *
 * The switch used to change only the WIDTH of the bars: in `%` every row went full-width while the
 * number beside it stayed an episode count, so the control looked broken. A percentage needs a
 * denominator, and the honest one differs by level — a top-level branch is a share of the card's
 * total, a segment is a share of its own parent. Both are passed in explicitly for that reason.
 */
function formatCount(value: number, mode: "n" | "pct", total: number): string {
  if (mode === "n") return String(value);
  if (!total) return "—";
  const share = (value / total) * 100;
  return `${share >= 10 ? Math.round(share) : Math.round(share * 10) / 10}\u202f%`;
}

function Count({
  value,
  mode = "n",
  total,
  className,
}: {
  value: number;
  mode?: "n" | "pct";
  /** The denominator this value is a share of. */
  total?: number;
  className?: string;
}) {
  return (
    <span className={cn("tabular-nums text-xs font-medium text-slate-700", className)}>
      {formatCount(value, mode, total ?? 0)}
    </span>
  );
}

/** Emitted wherever a level was folded. A cut nobody mentions reads as a complete list. */
function TruncationNote({ node, indent = 0 }: { node: CompositionNode | null; indent?: number }) {
  if (!node) return null;
  return (
    <div
      className="py-1 text-2xs text-slate-400"
      style={{ paddingLeft: indent ? `${indent * 16}px` : undefined }}
    >
      {/* No `episodes` on the sentinel: what it cut is under the anonymity threshold. */}
      <Trans>
        + {node.groups ?? 0} valeurs, {node.episodes ?? <WithheldCount />} épisodes
      </Trans>
      {(node.suppressed_groups ?? 0) > 0 && (
        <span className="ml-1 text-slate-400">
          <Trans>· dont {node.suppressed_groups} sous le seuil d'anonymat</Trans>
        </span>
      )}
    </div>
  );
}

function ClickableLabel({
  value,
  label,
  dimension,
  onPick,
  className,
}: {
  /** The term a click filters the page on. */
  value: string;
  /** The term as the reader reads it. */
  label: string;
  dimension: string;
  onPick: RepresentationProps["onPick"];
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={() => onPick(dimension, value)}
      title={label}
      className={cn(
        "truncate text-left text-xs text-slate-700 hover:text-primary hover:underline",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded",
        className,
      )}
    >
      {label}
    </button>
  );
}

/* ── Empilement — the ranked stack ─────────────────────────────────────────────────────────────
   One row per value of the first dimension, segmented by the second. In `n` the bars share the
   volume scale, so a branch twice another's size is visibly twice; in `pct` each row is brought to
   100 % and the internal split is what is read. Same data, two questions. */
export function StackChart({ data, mode, onPick }: RepresentationProps) {
  const labelOf = useLabelOf(data);
  const nodes = realNodes(data.nodes);
  const segmentDimension = dimensionOf(
    nodes.flatMap((node) => node.children ?? []),
  );
  const scale = topScale(data.nodes);
  const grandTotal = nodes.reduce((sum, n) => sum + (n.episodes ?? 0), 0);
  const segmentKeys = useMemo(() => {
    const seen: string[] = [];
    for (const node of nodes)
      for (const child of realNodes(node.children ?? []))
        if (child.label && !seen.includes(child.label)) seen.push(child.label);
    return seen;
  }, [nodes]);

  return (
    <div>
      <div className="space-y-2">
        {nodes.map((node) => {
          const total = node.episodes ?? 0;
          const children = realNodes(node.children ?? []);
          const rowWidth = mode === "pct" ? 100 : (total / scale) * 100;
          const covered = children.reduce((sum, c) => sum + (c.episodes ?? 0), 0);
          return (
            <div key={node.key} className="grid grid-cols-[minmax(0,11rem)_1fr_auto] items-center gap-3">
              <ClickableLabel
                value={node.label ?? node.key}
                label={labelOf(node.dimension, node.label ?? node.key)}
                dimension={node.dimension}
                onPick={onPick}
              />
              <div className="relative h-4 w-full rounded-sm bg-slate-100">
                <div className="flex h-full overflow-hidden rounded-sm" style={{ width: `${rowWidth}%` }}>
                  {children.length === 0 ? (
                    <div className="h-full w-full" style={{ background: groupColor(0) }} />
                  ) : (
                    children.map((child) => {
                      const part = covered > 0 ? ((child.episodes ?? 0) / covered) * 100 : 0;
                      const index = segmentKeys.indexOf(child.label ?? "");
                      return (
                        <div
                          key={child.key}
                          title={`${labelOf(child.dimension, child.label ?? "")} · ${formatCount(child.episodes ?? 0, mode, covered)}`}
                          className="h-full cursor-pointer transition-opacity hover:opacity-80"
                          style={{ width: `${part}%`, background: groupColor(index) }}
                          onClick={() => onPick(child.dimension, child.label ?? "")}
                        />
                      );
                    })
                  )}
                </div>
                {/* The reader's own share of THIS bar, in the speciality perimeter. One rule and it
                    reads the same in both modes: the marker sits at mine/total of the bar drawn. A
                    caret rather than a second bar, because it is a position on the same quantity —
                    not a second series to compare by eye. */}
                {node.mine !== undefined && total > 0 && (
                  <span
                    title={`${node.mine} / ${total}`}
                    className="pointer-events-none absolute -top-0.5 h-5 w-0.5 rounded-sm bg-slate-900"
                    style={{ left: `calc(${(node.mine / total) * rowWidth}% - 1px)` }}
                  />
                )}
              </div>
              <Count value={total} mode={mode} total={grandTotal} />
            </div>
          );
        })}
      </div>
      <TruncationNote node={truncationOf(data.nodes)} />
      {(segmentKeys.length > 0 || nodes.some((n) => n.mine !== undefined)) && (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-100 pt-2">
          {segmentKeys.map((label, index) => (
            <span key={label} className="inline-flex items-center gap-1.5 text-2xs text-slate-500">
              <span className="h-2 w-2 rounded-sm" style={{ background: groupColor(index) }} />
              {labelOf(segmentDimension, label)}
            </span>
          ))}
          {nodes.some((n) => n.mine !== undefined) && (
            <span className="inline-flex items-center gap-1.5 text-2xs text-slate-500">
              <span className="h-3 w-0.5 rounded-sm bg-slate-900" />
              <Trans>ma part</Trans>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Matrice — the crossing ────────────────────────────────────────────────────────────────────
   Loses the nesting the ruler keeps, and gains density: on two wide vocabularies the information is
   in the empty cells. A zero is written as a dot, never left blank — "I never do this" is a fact
   about the practice, and a blank cell reads as a missing measurement. */
export function MatrixChart({ data, mode, onPick }: RepresentationProps) {
  const labelOf = useLabelOf(data);
  const rows = realNodes(data.nodes);
  const columnDimension = dimensionOf(
    rows.flatMap((row) => row.children ?? []),
  );
  const columns = useMemo(() => {
    const seen: string[] = [];
    for (const row of rows)
      for (const child of realNodes(row.children ?? []))
        if (child.label && !seen.includes(child.label)) seen.push(child.label);
    return seen;
  }, [rows]);
  const max = Math.max(
    1,
    ...rows.flatMap((r) => realNodes(r.children ?? []).map((c) => c.episodes ?? 0)),
  );

  if (columns.length === 0) return <StackChart {...({ data, mode, onPick } as RepresentationProps)} />;

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[32rem] border-collapse text-xs">
        <thead>
          <tr>
            <th className="sticky left-0 bg-white p-1 text-left font-medium text-slate-400" />
            {columns.map((column) => (
              <th key={column} className="p-1 text-left align-bottom font-medium text-slate-500">
                <span
                  className="block max-w-[6rem] truncate"
                  title={labelOf(columnDimension, column)}
                >
                  {labelOf(columnDimension, column)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const byLabel = new Map(realNodes(row.children ?? []).map((c) => [c.label ?? "", c]));
            return (
              <tr key={row.key} className="border-t border-slate-100">
                <td className="sticky left-0 max-w-[11rem] bg-white p-1">
                  <ClickableLabel
                    value={row.label ?? row.key}
                    label={labelOf(row.dimension, row.label ?? row.key)}
                    dimension={row.dimension}
                    onPick={onPick}
                    className="block max-w-[11rem]"
                  />
                </td>
                {columns.map((column) => {
                  const cell = byLabel.get(column);
                  const n = cell?.episodes ?? 0;
                  return (
                    <td key={column} className="p-1">
                      {n === 0 ? (
                        // A zero is a dot, never a blank cell: "I never do this" is a fact about the
                        // practice, and a blank reads as a missing measurement.
                        <span className="text-slate-300">·</span>
                      ) : (
                        <span
                          className="inline-block rounded px-1.5 py-0.5 tabular-nums font-medium"
                          style={{
                            background: `rgba(23, 82, 183, ${0.08 + 0.5 * (n / max)})`,
                            color: n / max > 0.55 ? "#fff" : "#1e293b",
                          }}
                        >
                          {formatCount(n, mode, row.episodes ?? 0)}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <TruncationNote node={truncationOf(data.nodes)} />
    </div>
  );
}

/* ── Règle d'intervalles ───────────────────────────────────────────────────────────────────────
   The only form that does not lose the nesting of anatomical extents: L4-L5 and L3-S1 are two rows
   in a matrix with nothing relating them, when one contains the other. Here each operated extent is
   a segment laid on the real axis, so containment and overlap are visible without being computed. */
export function RulerChart({ data, mode, onPick }: RepresentationProps) {
  const labelOf = useLabelOf(data);
  const intervalIndex = data.dimensions.findIndex((d) => d.kind === "intervalle");
  const axis = intervalIndex >= 0 ? data.dimensions[intervalIndex].axis : [];
  const rows = realNodes(data.nodes);

  /* The segments are wherever the interval dimension sits, which is NOT always the second level:
     asked for `indication × geste × niveaux` the spans live three deep. Collecting the span-bearing
     descendants of each row rather than its direct children is what makes the ruler work at every
     depth — the first version read level two, found no span there, and drew an empty card. */
  const segmentsOf = useCallback((node: CompositionNode): CompositionNode[] => {
    const found: CompositionNode[] = [];
    const walk = (current: CompositionNode) => {
      if (current.span) {
        found.push(current);
        return;
      }
      for (const child of realNodes(current.children ?? [])) walk(child);
    };
    for (const child of realNodes(node.children ?? [])) walk(child);
    // Several branches can reach the same extent (two gestures both operating L4-L5); merged so the
    // ruler draws one segment per extent with the episodes behind it, not two stacked rectangles.
    const merged = new Map<string, CompositionNode>();
    for (const item of found) {
      const key = item.label ?? item.key;
      const existing = merged.get(key);
      if (existing) existing.episodes = (existing.episodes ?? 0) + (item.episodes ?? 0);
      else merged.set(key, { ...item });
    }
    return [...merged.values()].sort((a, b) => (b.episodes ?? 0) - (a.episodes ?? 0));
  }, []);

  // Only the stretch of the axis actually used, so a lumbar-only practice is not drawn as a sliver
  // at the bottom of the whole spine.
  const used = useMemo(() => {
    const spans = rows.flatMap((row) =>
      segmentsOf(row)
        .map((c) => c.span)
        .filter((s): s is { lo: number; hi: number } => !!s),
    );
    if (spans.length === 0) return { lo: 0, hi: Math.max(0, axis.length - 1) };
    return {
      lo: Math.max(0, Math.min(...spans.map((s) => s.lo)) - 1),
      hi: Math.min(axis.length - 1, Math.max(...spans.map((s) => s.hi)) + 1),
    };
  }, [rows, axis.length, segmentsOf]);

  if (axis.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-slate-400">
        <Trans>Aucun axe anatomique déclaré pour cette dimension</Trans>
      </p>
    );
  }

  const rulerTotal = rows.reduce((sum, r) => sum + (r.episodes ?? 0), 0);
  const width = Math.max(1, used.hi - used.lo + 1);
  const place = (lo: number, hi: number) => ({
    left: `${((lo - used.lo) / width) * 100}%`,
    width: `${((hi - lo + 1) / width) * 100}%`,
  });

  /* Overlapping extents go on separate lanes.
     L3-L4 and L4-L5 genuinely overlap on the axis — that is the information the ruler exists to
     show — but drawn on one line they cover each other and their two counts land on the same pixels.
     Greedy interval packing: each segment takes the first lane it does not collide in, so the
     overlap stays visible as two bars that share a column instead of two numbers that share a spot. */
  const laneOf = (segments: CompositionNode[]) => {
    const laneEnds: number[] = [];
    return segments.map((segment) => {
      const span = segment.span;
      if (!span) return 0;
      let lane = laneEnds.findIndex((end) => span.lo > end);
      if (lane === -1) lane = laneEnds.length;
      laneEnds[lane] = span.hi;
      return lane;
    });
  };

  const LANE_HEIGHT = 18;

  return (
    <div>
      <div className="mb-2 grid grid-cols-[minmax(0,9rem)_1fr_auto] items-end gap-3">
        <span />
        <div className="relative h-4">
          {axis.slice(used.lo, used.hi + 1).map((position, index) => (
            <span
              key={position}
              className="absolute text-3xs text-slate-400"
              style={{ left: `${(index / width) * 100}%` }}
            >
              {position}
            </span>
          ))}
        </div>
        <span />
      </div>
      <div className="space-y-2">
        {rows.map((row) => {
          // Widest extent first: the long constructs settle on the top lane and the short ones fill
          // in under them, which reads as a hierarchy rather than as a staircase.
          const segments = segmentsOf(row)
            .filter((c) => c.span)
            .sort((a, b) => (a.span?.lo ?? 0) - (b.span?.lo ?? 0));
          const lanes = laneOf(segments);
          const laneCount = Math.max(1, ...lanes.map((l) => l + 1));
          return (
            <div key={row.key} className="grid grid-cols-[minmax(0,9rem)_1fr_auto] items-start gap-3">
              <ClickableLabel
                value={row.label ?? row.key}
                label={labelOf(row.dimension, row.label ?? row.key)}
                dimension={row.dimension}
                onPick={onPick}
              />
              <div
                className="relative rounded-sm bg-slate-50"
                style={{ height: `${laneCount * LANE_HEIGHT + 4}px` }}
              >
                {segments.map((child, index) => {
                  const span = child.span;
                  if (!span) return null;
                  const spanWidth = ((span.hi - span.lo + 1) / width) * 100;
                  /* The figure goes INSIDE its segment. The first threshold for pushing it outside
                     was set by eye at 14 % and was far too cautious: measured on the demo the
                     narrowest extent is 8 % of the row — about 45 px — which holds two digits at
                     this size several times over. Only a sliver genuinely too small for two
                     characters drops the number, and it keeps it on hover. */
                  const inside = spanWidth >= 4;
                  const text = formatCount(child.episodes ?? 0, mode, row.episodes ?? 0);
                  return (
                    <button
                      key={child.key}
                      type="button"
                      onClick={() => onPick(child.dimension, child.label ?? "")}
                      title={`${labelOf(child.dimension, child.label ?? "")} · ${text}`}
                      className="absolute h-4 rounded-sm text-3xs font-medium text-white transition-opacity hover:opacity-80"
                      style={{
                        ...place(span.lo, span.hi),
                        top: `${lanes[index] * LANE_HEIGHT + 2}px`,
                        background: groupColor(index),
                        opacity: 0.55 + 0.45 * Math.min(1, (child.episodes ?? 0) / (row.episodes || 1)),
                      }}
                    >
                      {inside && (
                        <span className="flex h-full items-center justify-center px-0.5 tabular-nums leading-none">
                          {text}
                        </span>
                      )}
                    </button>
                  );
                })}
                {segments.length === 0 && (
                  <span className="absolute inset-y-0 left-1 flex items-center text-3xs text-slate-400">
                    <Trans>aucun niveau résolu</Trans>
                  </span>
                )}
              </div>
              <Count value={row.episodes ?? 0} mode={mode} total={rulerTotal} />
            </div>
          );
        })}
      </div>
      <TruncationNote node={truncationOf(data.nodes)} />
    </div>
  );
}

/* ── Emboîtement — the tree ────────────────────────────────────────────────────────────────────
   Three or more dimensions. Only the first level is open on arrival, every level is ranked and
   folded after N with the remainder written, and the bars share ONE scale from top to bottom — a
   scale per level would make a sub-group look as large as its parent. */
export function NestTree({ data, mode, onPick, expanded, onToggleExpand }: RepresentationProps) {
  const labelOf = useLabelOf(data);
  const scale = topScale(data.nodes);

  const renderLevel = (nodes: CompositionNode[], depth: number, path: string, parent: number) => (
    <>
      {realNodes(nodes).map((node) => {
        const key = `${path}/${node.key}`;
        const open = expanded.has(key);
        const children = node.children ?? [];
        const hasChildren = realNodes(children).length > 0;
        return (
          <Fragment key={key}>
            <div
              className="grid grid-cols-[1fr_8rem_auto] items-center gap-3 py-0.5"
              style={{ paddingLeft: `${depth * 16}px` }}
            >
              <div className="flex min-w-0 items-center gap-1">
                {hasChildren ? (
                  <button
                    type="button"
                    onClick={() => onToggleExpand(key)}
                    aria-expanded={open}
                    className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  >
                    {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  </button>
                ) : (
                  <span className="w-4 shrink-0" />
                )}
                <ClickableLabel
                  value={node.label ?? node.key}
                  label={labelOf(node.dimension, node.label ?? node.key)}
                  dimension={node.dimension}
                  onPick={onPick}
                />
              </div>
              <div className="h-3 rounded-sm bg-slate-100">
                <div
                  className="h-full rounded-sm"
                  style={{
                    width: `${((node.episodes ?? 0) / scale) * 100}%`,
                    background: groupColor(depth),
                  }}
                />
              </div>
              <Count value={node.episodes ?? 0} mode={mode} total={parent} />
            </div>
            {open && hasChildren && renderLevel(children, depth + 1, key, node.episodes ?? 0)}
          </Fragment>
        );
      })}
      <TruncationNote node={truncationOf(nodes)} indent={depth} />
    </>
  );

  const rootTotal = realNodes(data.nodes).reduce((sum, n) => sum + (n.episodes ?? 0), 0);
  return <div>{renderLevel(data.nodes, 0, "", rootTotal)}</div>;
}

/* ── Flux — the ribbon ─────────────────────────────────────────────────────────────────────────
   Kept because it says the SEQUENCE better than anything else, and offered rather than imposed
   because no number can be read off it. The counts are written on the nodes for that reason. */
export function FlowChart({ data, onPick }: RepresentationProps) {
  const labelOf = useLabelOf(data);
  const rows = realNodes(data.nodes);
  const rightDimension = dimensionOf(rows.flatMap((row) => row.children ?? []));
  const total = Math.max(1, rows.reduce((sum, r) => sum + (r.episodes ?? 0), 0));
  const height = Math.max(160, rows.length * 34);

  let leftCursor = 0;
  const bands = rows.map((row) => {
    const span = ((row.episodes ?? 0) / total) * height;
    const band = { node: row, y0: leftCursor, y1: leftCursor + span };
    leftCursor += span;
    return band;
  });

  const rightTotals = new Map<string, number>();
  for (const row of rows)
    for (const child of realNodes(row.children ?? []))
      rightTotals.set(child.label ?? "", (rightTotals.get(child.label ?? "") ?? 0) + (child.episodes ?? 0));
  const rightOrder = [...rightTotals.entries()].sort((a, b) => b[1] - a[1]);
  let rightCursor = 0;
  const rightBands = new Map<string, { y0: number; y1: number; n: number }>();
  for (const [label, n] of rightOrder) {
    const span = (n / total) * height;
    rightBands.set(label, { y0: rightCursor, y1: rightCursor + span, n });
    rightCursor += span;
  }

  const offsets = new Map<string, number>();

  return (
    <div className="overflow-x-auto">
      <svg width="100%" height={height + 8} viewBox={`0 0 600 ${height + 8}`} className="min-w-[30rem]">
        {bands.map((band, index) => {
          let sourceOffset = 0;
          const name = labelOf(band.node.dimension, band.node.label ?? "");
          return (
            <Fragment key={band.node.key}>
              {realNodes(band.node.children ?? []).map((child) => {
                const label = child.label ?? "";
                const target = rightBands.get(label);
                if (!target) return null;
                const share = ((child.episodes ?? 0) / total) * height;
                const sy0 = band.y0 + sourceOffset;
                sourceOffset += share;
                const ty0 = target.y0 + (offsets.get(label) ?? 0);
                offsets.set(label, (offsets.get(label) ?? 0) + share);
                return (
                  <path
                    key={child.key}
                    d={`M 190 ${sy0} C 320 ${sy0}, 280 ${ty0}, 410 ${ty0} L 410 ${ty0 + share} C 280 ${ty0 + share}, 320 ${sy0 + share}, 190 ${sy0 + share} Z`}
                    fill={groupColor(index)}
                    opacity={0.28}
                  />
                );
              })}
              <rect x={180} y={band.y0} width={10} height={Math.max(1, band.y1 - band.y0)} fill={groupColor(index)} />
              <text
                x={174}
                y={(band.y0 + band.y1) / 2 + 3}
                textAnchor="end"
                className="cursor-pointer fill-slate-600 text-[10px]"
                onClick={() => onPick(band.node.dimension, band.node.label ?? "")}
              >
                {name.slice(0, 26)} · {band.node.episodes}
              </text>
            </Fragment>
          );
        })}
        {[...rightBands.entries()].map(([label, band], index) => (
          <Fragment key={label}>
            <rect x={410} y={band.y0} width={10} height={Math.max(1, band.y1 - band.y0)} fill={groupColor(index)} />
            <text x={426} y={(band.y0 + band.y1) / 2 + 3} className="fill-slate-600 text-[10px]">
              {labelOf(rightDimension, label).slice(0, 24)} · {band.n}
            </text>
          </Fragment>
        ))}
      </svg>
      <TruncationNote node={truncationOf(data.nodes)} />
    </div>
  );
}

/* ── Aires empilées / courbe de volume ─────────────────────────────────────────────────────────
   A date dimension is bucketed to the year by the server, so this reads volume over time rather
   than 341 categories of one. */
export function AreaChart({ data, mode, onPick }: RepresentationProps) {
  const labelOf = useLabelOf(data);
  const rows = [...realNodes(data.nodes)].sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""));
  const segmentKeys = useMemo(() => {
    const seen: string[] = [];
    for (const row of rows)
      for (const child of realNodes(row.children ?? []))
        if (child.label && !seen.includes(child.label)) seen.push(child.label);
    return seen;
  }, [rows]);
  const scale = Math.max(1, ...rows.map((r) => r.episodes ?? 0));
  const areaTotal = rows.reduce((sum, r) => sum + (r.episodes ?? 0), 0);
  const segmentDimension = dimensionOf(
    rows.flatMap((row) => row.children ?? []),
  );

  return (
    <div>
      <div className="flex items-end gap-2" style={{ height: "11rem" }}>
        {rows.map((row) => {
          const total = row.episodes ?? 0;
          const children = realNodes(row.children ?? []);
          const covered = children.reduce((sum, c) => sum + (c.episodes ?? 0), 0);
          const columnHeight = mode === "pct" ? 100 : (total / scale) * 100;
          return (
            <div key={row.key} className="flex h-full min-w-0 flex-1 flex-col justify-end">
              <span className="mb-1 text-center text-2xs tabular-nums text-slate-500">
                {formatCount(total, mode, areaTotal)}
              </span>
              <div
                className="flex w-full flex-col-reverse overflow-hidden rounded-sm bg-slate-100"
                style={{ height: `${columnHeight}%` }}
              >
                {children.length === 0 ? (
                  <div className="w-full flex-1" style={{ background: groupColor(0) }} />
                ) : (
                  children.map((child) => (
                    <div
                      key={child.key}
                      title={`${labelOf(child.dimension, child.label ?? "")} · ${child.episodes}`}
                      className="w-full"
                      style={{
                        height: `${covered > 0 ? ((child.episodes ?? 0) / covered) * 100 : 0}%`,
                        background: groupColor(segmentKeys.indexOf(child.label ?? "")),
                      }}
                    />
                  ))
                )}
              </div>
              <button
                type="button"
                onClick={() => onPick(row.dimension, row.label ?? "")}
                className="mt-1 truncate text-center text-2xs text-slate-500 hover:text-primary"
              >
                {labelOf(row.dimension, row.label ?? "")}
              </button>
            </div>
          );
        })}
      </div>
      <TruncationNote node={truncationOf(data.nodes)} />
      {segmentKeys.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-100 pt-2">
          {segmentKeys.map((label, index) => (
            <span key={label} className="inline-flex items-center gap-1.5 text-2xs text-slate-500">
              <span className="h-2 w-2 rounded-sm" style={{ background: groupColor(index) }} />
              {labelOf(segmentDimension, label)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function CurveChart({ data, onPick }: RepresentationProps) {
  const labelOf = useLabelOf(data);
  const rows = [...realNodes(data.nodes)].sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""));
  const scale = Math.max(1, ...rows.map((r) => r.episodes ?? 0));
  const points = rows
    .map((row, index) => {
      const x = rows.length > 1 ? (index / (rows.length - 1)) * 560 + 20 : 300;
      const y = 130 - ((row.episodes ?? 0) / scale) * 110;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <div className="overflow-x-auto">
      <svg width="100%" height={160} viewBox="0 0 600 160" className="min-w-[24rem]">
        <polyline points={points} fill="none" stroke="#1752B7" strokeWidth={2} />
        {rows.map((row, index) => {
          const x = rows.length > 1 ? (index / (rows.length - 1)) * 560 + 20 : 300;
          const y = 130 - ((row.episodes ?? 0) / scale) * 110;
          return (
            <Fragment key={row.key}>
              <circle cx={x} cy={y} r={3.5} fill="#1752B7" />
              <text x={x} y={y - 8} textAnchor="middle" className="fill-slate-600 text-[10px] tabular-nums">
                {row.episodes}
              </text>
              <text
                x={x}
                y={150}
                textAnchor="middle"
                className="cursor-pointer fill-slate-500 text-[10px]"
                onClick={() => onPick(row.dimension, row.label ?? "")}
              >
                {labelOf(row.dimension, row.label ?? "")}
              </text>
            </Fragment>
          );
        })}
      </svg>
    </div>
  );
}

/* ── Distribution — the histogram of a measure ─────────────────────────────────────────────── */
export function DistributionChart({ data }: RepresentationProps) {
  const { t } = useLingui();
  const distribution = data.distribution;
  // Read in aggregate, too few values to draw at all: `n` is withheld and there is no bin.
  if (distribution && distribution.n === null) {
    return (
      <p className="py-8 text-center text-sm text-slate-400">
        <Trans>Trop peu de valeurs mesurées sur cette sélection : effectif sous le seuil d'anonymat.</Trans>
      </p>
    );
  }
  if (!distribution || distribution.bins.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-slate-400">
        <Trans>Aucune valeur mesurée sur cette sélection</Trans>
      </p>
    );
  }
  // The scale is read off the bins that are shown; a withheld bin is drawn hatched, at no height of its own.
  const scale = Math.max(1, ...distribution.bins.map((b) => b.n ?? 0));
  const round = (v: number) => (Math.abs(v - Math.round(v)) < 0.05 ? Math.round(v) : Number(v.toFixed(1)));
  return (
    <div>
      <div className="flex items-end gap-1" style={{ height: "10rem" }}>
        {distribution.bins.map((bin) => (
          <div key={`${bin.lo}`} className="flex h-full min-w-0 flex-1 flex-col justify-end">
            <span className="mb-1 text-center text-2xs tabular-nums text-slate-500">
              {bin.n ?? <WithheldCount />}
            </span>
            {bin.n === null ? (
              <div
                title={t`Effectif inférieur au seuil d'anonymat, masqué`}
                className="h-2 w-full rounded-t-sm border border-dashed border-slate-300"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(45deg, rgb(203 213 225) 0 2px, transparent 2px 5px)",
                }}
              />
            ) : (
              <div
                className="w-full rounded-t-sm"
                style={{ height: `${(bin.n / scale) * 100}%`, background: groupColor(0) }}
              />
            )}
            <span className="mt-1 truncate text-center text-3xs text-slate-400">
              {bin.label ?? round(bin.lo)}
            </span>
          </div>
        ))}
      </div>
      <p className="mt-2 text-2xs text-slate-500">
        <Trans>
          {distribution.n ?? <WithheldCount />} valeurs · médiane {round(distribution.median ?? 0)}
        </Trans>
      </p>
    </div>
  );
}

/* ── Tableau compté — the floor ────────────────────────────────────────────────────────────────
   Always in the list of offered forms, which is what guarantees the card never refuses a question. */
export function CountTable({ data, mode, onPick }: RepresentationProps) {
  const labelOf = useLabelOf(data);
  const flat: { path: string[]; dimension: string; label: string; episodes: number; patients: number }[] = [];
  const walk = (nodes: CompositionNode[], path: string[]) => {
    for (const node of realNodes(nodes)) {
      const next = [...path, node.label ?? node.key];
      const children = realNodes(node.children ?? []);
      if (children.length === 0) {
        flat.push({
          path: next,
          dimension: node.dimension,
          label: node.label ?? node.key,
          episodes: node.episodes ?? 0,
          patients: node.patients ?? 0,
        });
      } else {
        walk(children, next);
      }
    }
  };
  walk(data.nodes, []);
  flat.sort((a, b) => b.episodes - a.episodes);
  const tableTotal = flat.reduce((sum, r) => sum + r.episodes, 0);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-slate-200 text-left text-slate-500">
            {data.dimensions.map((dimension: PathwayDimension) => (
              <th key={dimension.key} className="py-1 pr-3 font-medium">
                {dimension.label}
              </th>
            ))}
            <th className="py-1 pr-3 text-right font-medium">
              <Trans>Épisodes</Trans>
            </th>
            <th className="py-1 text-right font-medium">
              <Trans>Patients</Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          {flat.map((row) => (
            <tr key={row.path.join("›")} className="border-b border-slate-50">
              {data.dimensions.map((dimension, index) => (
                <td key={dimension.key} className="max-w-[12rem] py-1 pr-3">
                  {row.path[index] !== undefined && (
                    <ClickableLabel
                      value={row.path[index]}
                      label={labelOf(dimension.key, row.path[index])}
                      dimension={dimension.key}
                      onPick={onPick}
                      className="block max-w-[12rem]"
                    />
                  )}
                </td>
              ))}
              <td className="py-1 pr-3 text-right tabular-nums text-slate-700">
                {formatCount(row.episodes, mode, tableTotal)}
              </td>
              <td className="py-1 text-right tabular-nums text-slate-500">{row.patients}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <TruncationNote node={truncationOf(data.nodes)} />
    </div>
  );
}
